import React from "react";
import headerIcon from "../../../../assets/icons/report-case-analysis-icon.svg";
import beyondIcon from "../../../../assets/images/report_caseAnalysis_beyond.svg";
import meetIcon from "../../../../assets/images/meetIcon.svg";
import belowIcon from "../../../../assets/images/report_caseAnalysis_below.svg";

// 1) Import the new type from your "types.ts"
import { ComponentAnalysis, StatusLevel } from "../../types";

interface CaseAnalysisProps {
  // This is the new shape from your new interface
  componentAnalysis?: ComponentAnalysis;
}

const CaseAnalysis: React.FC<CaseAnalysisProps> = ({ componentAnalysis }) => {
  // 2) If `componentAnalysis` is undefined, we'll default to an empty object
  //    or bail out. Adjust how you prefer to handle missing data.
  if (!componentAnalysis) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm h-full flex flex-col justify-center items-center">
        <p>No component analysis data available.</p>
      </div>
    );
  }

  // 3) Transform the new shape into an array of sections (title, overallStatus, criteria[])
  //    so we can map over them with the same structure as your old code.
  //    Each "criteria" is turned into an array of { name, status } from the nested object.
  const sections = [
    {
      title: "Opening & Structure",
      overallStatus: componentAnalysis.opening_and_structure.overallStatus,
      criteria: [
        componentAnalysis.opening_and_structure.criteria.clarifying_questions,
        componentAnalysis.opening_and_structure.criteria.framework,
        componentAnalysis.opening_and_structure.criteria.hypothesis,
      ],
    },
    {
      title: "Brainstorming",
      overallStatus: componentAnalysis.brainstorming.overallStatus,
      criteria: [
        componentAnalysis.brainstorming.criteria.reasonableness,
        componentAnalysis.brainstorming.criteria.creativity,
        componentAnalysis.brainstorming.criteria.depth_and_breadth,
      ],
    },
    {
      title: "Quantitative Analysis",
      overallStatus: componentAnalysis.quantitative_analysis.overallStatus,
      criteria: [
        componentAnalysis.quantitative_analysis.criteria.accuracy,
        componentAnalysis.quantitative_analysis.criteria.insights,
        componentAnalysis.quantitative_analysis.criteria.speed,
      ],
    },
    {
      title: "Final Recommendation",
      overallStatus: componentAnalysis.final_recommendation.overallStatus,
      criteria: [
        componentAnalysis.final_recommendation.criteria.structure_and_evidence,
        componentAnalysis.final_recommendation.criteria.risk_consideration,
        componentAnalysis.final_recommendation.criteria.next_steps,
      ],
    },
  ];

  // 4) A helper to pick the correct icon (or fallback) based on status
  const getStatusSymbol = (status: StatusLevel) => {
    // Handle null or undefined cases
    if (!status) {
      return <span>N/A</span>;
    }

    const normalized = status.toLowerCase();

    // Check for "exceeds" variations
    if (normalized.includes("exceed") || normalized.includes("beyond")) {
      return <img src={beyondIcon} alt="Beyond Expectations" />;
    }

    // Check for "below" variations
    if (normalized.includes("below") || normalized.includes("develop")) {
      return <img src={belowIcon} alt="Below Expectations" />;
    }

    // Check for "meets" variations
    if (normalized.includes("meet")) {
      return <img src={meetIcon} alt="Meets Expectations" />;
    }

    // Fallback for unrecognized status
    return <span className="text-[var(--color-primary)]">{status}</span>;
  };

  return (
    <div
      className="bg-white rounded-lg shadow-sm h-full flex flex-col"
      style={{
        border: "1px solid #E6E8EB",
      }}
    >
      {/* Header */}
      <div className="bg-[#F5F6F7] p-4 rounded-t-lg">
        <div className="flex items-center gap-2">
          <img
            src={headerIcon}
            className="w-5 h-5"
            style={{
              filter:
                "invert(27%) sepia(93%) saturate(1862%) hue-rotate(227deg) brightness(89%) contrast(98%)",
            }}
            alt="Case Analysis Icon"
          />
          <h3 className="text-xl font-semibold">
            Case Component Analysis Overview
          </h3>
        </div>
      </div>

      {/* Analysis Grid */}
      <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-6 p-6">
        {sections.map((section, index) => (
          <div key={index} className="flex flex-col">
            {/* Section Title + Overall Status Icon */}
            <div className="flex justify-between items-center mb-3">
              <div className="inline-block px-3 py-1 bg-[var(--color-bg-secondary)] rounded-[13px]">
                <span className="text-gray-600 font-light">
                  {section.title}
                </span>
              </div>
              <span className="text-xl font-medium">
                {getStatusSymbol(section.overallStatus)}
              </span>
            </div>

            {/* Criteria List */}
            <div className="space-y-2">
              {section.criteria.map((criterion, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-gray-600 px-3 text-sm">
                    {criterion.name}
                  </span>
                  <span className="text-xl font-medium">
                    {getStatusSymbol(criterion.status)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-6 p-6">
        <div className="flex items-center gap-2">
          <img src={meetIcon} alt="Meets" />
          <span className="text-sm text-gray-600">Meets Expectations</span>
        </div>
        <div className="flex items-center gap-2">
          <img src={beyondIcon} alt="Beyond" />
          <span className="text-sm text-gray-600">Beyond Expectations</span>
        </div>
        <div className="flex items-center gap-2">
          <img src={belowIcon} alt="Below" />
          <span className="text-sm text-gray-600">Below Expectations</span>
        </div>
      </div>
    </div>
  );
};

export default CaseAnalysis;
